import { getMomentDate } from '../utils/commonUtils';
import { DATE_TIME_FORMAT, CURRENCY } from '../constants/Define';
import BaseModel from './BaseModel';
import Product from './Product';
import Property from './Property';
import GroupCategory from './GroupCategory';
import { find, toArray } from 'lodash';
import i18n from '../constants/initLng';

export default class Group extends BaseModel {
    constructor(data = {}) {
        super(data);
        this.setData(data)
    }

    setData(data = this.__data__) {
        this.id = data.id || undefined;
        this.name = data.name || this.translate.name || undefined;
        this.description = data.description || this.translate.description || undefined;
        this.conditionOfUse = data.conditionOfUse || this.translate.conditionOfUse || undefined;
        this.discountValue = data.discountValue || 0;
        this.endDate = getMomentDate(data.endDate);
        this.groupCode = data.groupCode || undefined;
        this.highlight = data.highlight || this.translate.highlight || this.translate.highLight || undefined;
        this.images = Array.isArray(data.images || this.translate.images) ? data.images || this.translate.images : [];
        this.imagesView = data.imagesView || this.translate.imagesView || [];
        this.isAvailable = data.isAvailable || false;
        this.isHot = data.isHot || false;
        this.isNew = data.isNew || false;
        this.isSaleToWeb = data.isSaleToWeb || false;
        this.isSpecial = data.isSpecial || false;
        this.createVoucherNumber = data.createVoucherNumber;
        this.limitNumberOfSalesPerUser = data.limitNumberOfSalesPerUser || 0;
        this.limitNumberOfTimes = data.limitNumberOfTimes || 0;
        this.maxDiscountValue = data.maxDiscountValue || 0;
        this.maxOrderValue = data.maxOrderValue || 0;
        this.minOrderValue = data.minOrderValue || 0;
        this.operationManuals = data.operationManuals || this.translate.operationManuals || undefined;
        this.originalPrice = data.originalPrice || 0;
        this.numberOfBookingPeriod = data.numberOfBookingPeriod || 0;
        this.products = (data.products || []).map(item => new Product(item));
        this.properties = (data.properties || []).map(item => new Property(item));
        this.categories = (data.categories || []).map(item => new GroupCategory(item));
        this.publishDate = getMomentDate(data.publishDate);
        this.remainVouchers = data.remainVouchers || [];
        this.saleEndDate = getMomentDate(data.saleEndDate);
        this.bookingPeriod = getMomentDate(data.bookingPeriod);
        this.salePrice = data.salePrice || 0;
        this.saleStartDate = getMomentDate(data.saleStartDate);
        this.shortDescription = data.shortDescription || this.translate.shortDescription || undefined;
        this.startDate = getMomentDate(data.startDate);
        this.thumbImage = data.thumbImage || this.translate.thumbImage || undefined;
        this.thumbImageView = data.thumbImageView || (this.translate.thumbImage ? this.translate.thumbImageView : undefined) || undefined;
        this.voucherType = data.voucherType || undefined;
        this.groupPrices = new GroupPrices(data.groupPrices || []);
        this.urlSlug = data.urlSlug || undefined;
        this.order = data.order || 0;
        this.stayStartDate = getMomentDate(data.stayStartDate);
        this.stayEndDate = getMomentDate(data.stayEndDate);
        this.tag = this.translate.tag || data.tag || undefined;
        this.installmentFee = data.installmentFee;
        this.installmentLimit = data.installmentLimit;
    }

    export(hasTranslate = false) {
        const result = super.export({
            id: this.id,
            name: this.name,
            description: this.description,
            conditionOfUse: this.conditionOfUse,
            discountValue: this.discountValue,
            endDate: this.endDate && (this.endDate).format(DATE_TIME_FORMAT.SHORT_DATE_EXPORT),
            groupCode: this.groupCode,
            highlight: this.highlight,
            images: this.images,
            isAvailable: this.isAvailable,
            isHot: this.isHot,
            isNew: this.isNew,
            isSaleToWeb: this.isSaleToWeb,
            isSpecial: this.isSpecial,
            createVoucherNumber: this.createVoucherNumber,
            limitNumberOfSalesPerUser: this.limitNumberOfSalesPerUser,
            limitNumberOfTimes: this.limitNumberOfTimes,
            maxDiscountValue: this.maxDiscountValue,
            maxOrderValue: this.maxOrderValue,
            minOrderValue: this.minOrderValue,
            operationManuals: this.operationManuals,
            originalPrice: this.originalPrice,
            numberOfBookingPeriod: this.numberOfBookingPeriod,
            productIds: this.products.map(item => item.id),
            propertyIds: this.properties.map(item => item.id),
            categoryIds: this.categories.map(item => item.id),
            publishDate: this.publishDate && (this.publishDate).toISOString(),
            remainVoucher: this.remainVoucher,
            saleEndDate: this.saleEndDate && (this.saleEndDate).toISOString(),
            bookingPeriod: this.bookingPeriod && (this.bookingPeriod).toISOString(),
            salePrice: this.salePrice || undefined,
            saleStartDate: this.saleStartDate && (this.saleStartDate).toISOString(),
            shortDescription: this.shortDescription,
            startDate: this.startDate && (this.startDate).format(DATE_TIME_FORMAT.SHORT_DATE_EXPORT),
            thumbImage: this.thumbImage,
            voucherType: this.voucherType,
            groupPrices: this.groupPrices && this.groupPrices.export(),
            urlSlug: this.urlSlug,
            order: Number(this.order),
            stayStartDate: this.stayStartDate && (this.stayStartDate).format(DATE_TIME_FORMAT.SHORT_DATE_EXPORT),
            stayEndDate: this.stayEndDate && (this.stayEndDate).format(DATE_TIME_FORMAT.SHORT_DATE_EXPORT),
            tag: this.tag,
            installmentFee: this.installmentFee,
            installmentLimit: this.installmentLimit
        });
        if (hasTranslate) result.translate = () => {
            return {
                id: this.translate.id,
                groupId: this.translate.groupId || this.id,
                name: this.name,
                shortDescription: this.shortDescription,
                description: this.description,
                highLight: this.highlight,
                conditionOfUse: this.conditionOfUse,
                operationManuals: this.operationManuals,
                images: this.images,
                thumbImage: this.thumbImage,
                languageCode: this.languageCode,
                tag: this.tag
            }
        }
        return result;
    }
}

export class GroupPrices {
    constructor(items = []) {
        this.setData(items)
    }

    setData(items = []) {
        toArray(CURRENCY).forEach(key => {
            const data = find(items, item => item.currencyCode === key) || {};
            this[key] = {
                id: data.id || undefined,
                groupId: data.groupId || undefined,
                currencyCode: data.currencyCode || CURRENCY[key] || undefined,
                originalPrice: data.originalPrice,
                salePrice: data.salePrice,
                isDefault: data.isDefault || false,
                currencyName: this.getCurrencyName(data),
            }
        })
    }

    getCurrencyName(data = {}) {
        const currencyCode = find(toArray(CURRENCY), key => CURRENCY[key])
        return i18n.t(`currencies.${currencyCode}`);
    }

    export() {
        const result = [];
        toArray(CURRENCY).forEach(key => {
            const item = this[key];
            if (item && item.currencyCode && item.originalPrice) {
                result.push({
                    id: item.id,
                    groupId: item.groupId,
                    currencyCode: item.currencyCode,
                    originalPrice: item.originalPrice,
                    salePrice: item.salePrice,
                    isDefault: item.isDefault,
                })
            }
        })
        return result;
    }
}